import type { QueryParam } from '@exam-management/common/types';
import { QuestionGroupType, useSearchQuestionsQuery } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useNavigate } from '@exam-management/router';
import { ErrorBox, type GridCellParams } from '@shared/components';
import { useMemo } from 'react';

import { QContentListTable } from '../../components';
import { initialQContentList, QuestionCategoryInput } from '../../constants';
import { useQContentSearchParams } from '../../hooks';

import { columns, type Question } from './columns';

const CATEGORY_SEARCH_OPTIONS = {
  [QuestionCategoryInput.SINGLE_AND_LOQ]: [
    QuestionGroupType.Single,
    QuestionGroupType.LotOfQuestion,
  ],
  [QuestionCategoryInput.ITEM_SET]: [QuestionGroupType.ItemSet],
};

export default function QContentList() {
  const { qBankStructureInformation } = useQBankStructure();
  const [
    {
      page,
      pageSize,
      sort,
      qIds,
      category,
      part,
      subject,
      group,
      chapter,
      topic,
      subtopic,
      status,
    },
    setSearchParams,
  ] = useQContentSearchParams();
  const navigate = useNavigate();

  const groupTypeSearchOptions = useMemo(() => CATEGORY_SEARCH_OPTIONS[category], [category]);
  const levelItemId = subtopic || topic || chapter || group || subject || part;

  const {
    data,
    loading: isLoading,
    error,
  } = useSearchQuestionsQuery({
    variables: {
      search: {
        refQBankStructureId: qBankStructureInformation.id,
        refCode: qIds,
        questionGroupType: groupTypeSearchOptions ?? undefined,
        levelItemId,
        status: status ? [status] : [],
      },
      paging: {
        pageSize,
        currentPage: page,
      },
      order: {
        field: 'updatedAt',
        type: sort,
      },
    },
    fetchPolicy: 'network-only',
  });

  if (category === QuestionCategoryInput.ITEM_SET) {
    return null;
  }

  const handleSearchQueryParams = (queryParams: QueryParam[]) => {
    setSearchParams(queryParams);
  };
  const rows = data?.qctSearchQuestions.questions ?? [];
  const qContentCount = data?.qctSearchQuestions.total ?? 0;

  const navigateToQContent = (cell: GridCellParams<Question>) => {
    const { id, questionGroup } = cell.row;

    if (cell.field === 'moreAction') return;

    if (questionGroup?.type === QuestionGroupType.ItemSet) {
      return navigate(
        '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId',
        {
          params: {
            questionId: questionGroup.id,
            structureId: qBankStructureInformation.id,
            subQuestionId: id,
          },
        },
      );
    }

    navigate('/qbank-structures/:structureId/qcontents/single/:contentId', {
      params: { structureId: qBankStructureInformation.id, contentId: id },
    });
  };

  if (error) {
    return <ErrorBox />;
  }

  return (
    <QContentListTable
      columns={columns}
      rows={rows}
      loading={isLoading}
      hideFooter={qContentCount === 0 || isLoading}
      onCellClick={navigateToQContent}
      slotProps={{
        footer: {
          total: qContentCount,
          pagination: {
            page: page,
            count: qContentCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              handleSearchQueryParams([{ name: 'page', value: page }]);
            },
            onRowsPerPageChange: (event) => {
              const pageSize =
                parseInt(event.target.value) === initialQContentList.pageSize
                  ? false
                  : event.target.value;

              return handleSearchQueryParams([
                {
                  name: 'pageSize',
                  value: pageSize,
                },
                { name: 'page', value: 0 },
              ]);
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const field = sorting[0]?.field;
        const sort = sorting[0]?.sort?.toLowerCase() ?? undefined;
        handleSearchQueryParams([
          { name: 'field', value: field },
          { name: 'sort', value: sort },
        ]);
      }}
    />
  );
}
